import { useCallback } from 'react';
import { isAtlasUnfiledId } from '../constants';

/**
 * Canvas pointer composition plus node/canvas click and context-menu handlers.
 */
export function useAtlasCanvasEvents({
  onPointerDown,
  onPointerMove,
  onPointerUp,
  startMarquee,
  moveMarquee,
  endMarquee,
  moveFolderPointer,
  endFolderPointer,
  didPanRef,
  didMarqueeDragRef,
  didFolderDragRef,
  setExpandedId,
  setSelectedIds,
  setCtx,
}) {
  const handlePointerDownCanvas = useCallback(
    (e) => {
      if (e.button === 2 || (e.button === 0 && e.altKey)) {
        startMarquee(e);
        return;
      }
      onPointerDown(e);
    },
    [onPointerDown, startMarquee]
  );

  const handlePointerMoveCanvas = useCallback(
    (e) => {
      if (moveFolderPointer(e)) return;
      if (moveMarquee(e)) return;
      onPointerMove(e);
    },
    [moveFolderPointer, moveMarquee, onPointerMove]
  );

  const handlePointerUpCanvas = useCallback(
    (e) => {
      if (endFolderPointer(e)) return;
      if (endMarquee(e)) return;
      onPointerUp(e);
    },
    [endFolderPointer, endMarquee, onPointerUp]
  );

  const handleNodeClick = useCallback(
    (e, folderId) => {
      if (didPanRef.current) {
        didPanRef.current = false;
        return;
      }
      if (didMarqueeDragRef.current) return;
      if (didFolderDragRef.current) {
        didFolderDragRef.current = false;
        return;
      }
      e.stopPropagation();
      if (isAtlasUnfiledId(folderId)) {
        setSelectedIds(new Set([folderId]));
        setExpandedId((prev) => (prev === folderId ? null : folderId));
        setCtx(null);
        return;
      }
      if (e.shiftKey) {
        setSelectedIds((prev) => {
          const next = new Set([...prev].filter((id) => !isAtlasUnfiledId(id)));
          if (next.has(folderId)) next.delete(folderId);
          else next.add(folderId);
          return next;
        });
      } else {
        setSelectedIds(new Set([folderId]));
      }
      setExpandedId((prev) => (prev === folderId ? null : folderId));
      setCtx(null);
    },
    [didPanRef, didMarqueeDragRef, didFolderDragRef, setSelectedIds, setExpandedId, setCtx]
  );

  const handleCanvasClick = useCallback(() => {
    if (didPanRef.current) {
      didPanRef.current = false;
      return;
    }
    if (didMarqueeDragRef.current) {
      didMarqueeDragRef.current = false;
      return;
    }
    if (didFolderDragRef.current) {
      didFolderDragRef.current = false;
      return;
    }
    setExpandedId(null);
    setSelectedIds(new Set());
    setCtx(null);
  }, [didPanRef, didMarqueeDragRef, didFolderDragRef, setExpandedId, setSelectedIds, setCtx]);

  const handleNodeContextMenu = useCallback((e, folderId) => {
    e.preventDefault();
    e.stopPropagation();
    if (didMarqueeDragRef.current) {
      didMarqueeDragRef.current = false;
      return;
    }
    setExpandedId(folderId);
    setSelectedIds((prev) => {
      if (isAtlasUnfiledId(folderId)) return new Set([folderId]);
      if (prev.has(folderId)) {
        const next = new Set([...prev].filter((id) => !isAtlasUnfiledId(id)));
        return next.size === prev.size ? prev : next;
      }
      return new Set([folderId]);
    });
    setCtx({ x: e.clientX, y: e.clientY, folderId });
  }, [didMarqueeDragRef, setExpandedId, setSelectedIds, setCtx]);

  const handleCanvasContextMenu = useCallback((e) => {
    if (e.target.closest?.('.atlas__peek')) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    if (didMarqueeDragRef.current) {
      didMarqueeDragRef.current = false;
      return;
    }
    setExpandedId(null);
    setCtx({ x: e.clientX, y: e.clientY, folderId: null });
  }, [didMarqueeDragRef, setExpandedId, setCtx]);

  return {
    handlePointerDownCanvas,
    handlePointerMoveCanvas,
    handlePointerUpCanvas,
    handleNodeClick,
    handleCanvasClick,
    handleNodeContextMenu,
    handleCanvasContextMenu,
  };
}
